import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Table, Input, Button, Space, Tag, Modal, Form, message, Select, Drawer, Descriptions, InputNumber, Row, Col, Divider, DatePicker, Popconfirm, Tooltip, Badge, Tabs, Switch, Alert, Spin, Typography, Image } from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  StopOutlined,
  CheckCircleOutlined,
  MailOutlined,
  ReloadOutlined,
  DollarOutlined,
  FilterOutlined,
  KeyOutlined,
  SettingOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { authAPI, compactParams, playerAPI } from '../services/api';
import { formatRawFlowCurrency } from '../utils/displayMoney';

const { Search } = Input;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Text } = Typography;
const EMPTY_TEXT = '未获取';

const renderEmpty = (text = EMPTY_TEXT) => <Text type="secondary">{text}</Text>;

const formatNumber = (value, fallback = EMPTY_TEXT) => (
  Number.isFinite(Number(value)) ? Number(value).toLocaleString() : fallback
);

const formatDateTime = (value, { seconds = false, fallback = EMPTY_TEXT } = {}) => {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }

  if (seconds && Number(value) <= 0) {
    return fallback;
  }

  const date = seconds ? new Date(Number(value) * 1000) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  return date.toLocaleString('zh-CN');
};

const renderTableStack = (primary, secondary, meta) => (
  <div className="table-cell">
    <div className="table-cell-primary">{primary}</div>
    {secondary ? <div className="table-cell-secondary">{secondary}</div> : null}
    {meta ? <div className="table-cell-meta">{meta}</div> : null}
  </div>
);

const renderEmptyPanel = (title, subtitle) => (
  <div className="empty-state-panel">
    <div className="empty-state-title">{title}</div>
    {subtitle ? <div className="empty-state-subtitle">{subtitle}</div> : null}
  </div>
);

const getCategoryColor = (category) => {
  const colors = {
    '道具': 'blue',
    '装备': 'green',
    '英雄': 'purple',
    '战机装备': 'orange',
    '装饰': 'cyan',
    '试炼道具': 'volcano',
    '吃鸡道具': 'red',
    'GVG道具': 'geekblue',
    '全局资源': 'gold',
    '未收录': 'default'
  };
  return colors[category] || 'default';
};

const getQualityColor = (quality) => {
  const colors = {
    1: 'default',
    2: 'green',
    3: 'blue',
    4: 'purple',
    5: 'orange',
    6: 'red'
  };
  return colors[quality] || 'default';
};

const getQualityText = (quality) => {
  const texts = {
    1: '普通',
    2: '优秀',
    3: '稀有',
    4: '史诗',
    5: '传说',
    6: '神话'
  };
  return texts[quality] || `${quality}星`;
};

const buildPlayerListParams = ({ currentPage, pageSize, searchKeyword, sortConfig, filterValues }) => (
  compactParams({
    page: currentPage,
    limit: pageSize,
    keyword: searchKeyword,
    hasCharacter: 1,
    sort_field: sortConfig.field && sortConfig.order ? sortConfig.field : undefined,
    sort_order: sortConfig.field && sortConfig.order ? sortConfig.order : undefined,
    status: filterValues.status,
    target_zone: filterValues.server_id === 'all' ? 'all' : undefined,
    server_id: filterValues.server_id !== 'all' ? filterValues.server_id : undefined,
    start_date: filterValues.dateRange?.[0]?.format('YYYY-MM-DD'),
    end_date: filterValues.dateRange?.[1]?.format('YYYY-MM-DD')
  })
);

// 玩家管理组件
function PlayerTab() {
  const [loading, setLoading] = useState(false);
  const [players, setPlayers] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [sortConfig, setSortConfig] = useState({ field: '', order: null });
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [banVisible, setBanVisible] = useState(false);
  const [banForm] = Form.useForm();
  const [rechargeVisible, setRechargeVisible] = useState(false);
  const [rechargeForm] = Form.useForm();
  const rechargeBusy = useRef(false);
  const [rechargeSubmitting, setRechargeSubmitting] = useState(false);
  const [pendingRecharge, setPendingRecharge] = useState(false);
  const [accountingMode, setAccountingMode] = useState('gift');
  const [billingPagination, setBillingPagination] = useState({ page: 1, limit: 20, total: 0 });
  const rechargeStorageKey = (player) => `admin-direct-recharge:${player.server_id}:${player.game_id || player.id}`;
  const [mailVisible, setMailVisible] = useState(false);
  const [mailForm] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [advancedSearchVisible, setAdvancedSearchVisible] = useState(false);
  const [filterForm] = Form.useForm();
  const [detailLoading, setDetailLoading] = useState(false);
  const [playerResources, setPlayerResources] = useState(null);
  const [rechargeStats, setRechargeStats] = useState(null);
  const [rechargeLogs, setRechargeLogs] = useState([]);
  const [backpackItems, setBackpackItems] = useState([]);
  const [backpackKeyword, setBackpackKeyword] = useState('');
  const [backpackCategory, setBackpackCategory] = useState('');
  const [activeTab, setActiveTab] = useState('basic');
  const [resetPasswordVisible, setResetPasswordVisible] = useState(false);
  const [resetPasswordForm] = Form.useForm();
  const [integralConfigVisible, setIntegralConfigVisible] = useState(false);
  const [integralConfigForm] = Form.useForm();
  const [integralConfig, setIntegralConfig] = useState(null);
  const [useIntegral, setUseIntegral] = useState(false);
  const [safeCodeVisible, setSafeCodeVisible] = useState(false);
  const [safeCodeLoading, setSafeCodeLoading] = useState(false);
  const [safeCodeInfo, setSafeCodeInfo] = useState(null);
  const [zones, setZones] = useState([]);
  const [serverScope, setServerScope] = useState('all');
  const currentPage = pagination.current;
  const pageSize = pagination.pageSize;
  const backpackCategories = useMemo(() => (
    Array.from(new Set(backpackItems.map((item) => item.category).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'zh-CN'))
  ), [backpackItems]);
  const filteredBackpackItems = useMemo(() => {
    const keyword = backpackKeyword.trim().toLowerCase();

    return backpackItems.filter((item) => {
      const matchKeyword = !keyword
        || String(item.gid || '').includes(keyword)
        || String(item.item_name || '').toLowerCase().includes(keyword)
        || String(item.description || '').toLowerCase().includes(keyword);
      const matchCategory = !backpackCategory || item.category === backpackCategory;
      return matchKeyword && matchCategory;
    });
  }, [backpackCategory, backpackItems, backpackKeyword]);
  const filteredBackpackSummary = useMemo(() => ({
    total_types: filteredBackpackItems.length,
    total_count: filteredBackpackItems.reduce((sum, item) => sum + (Number(item.count) || 0), 0),
    expiring_types: filteredBackpackItems.filter((item) => Number(item.expire_time) > 0).length
  }), [filteredBackpackItems]);
  const renderAvatarPreview = (url, fallbackText) => (
    url ? (
      <Image
        width={72}
        height={72}
        src={url}
        alt="player-avatar"
        style={{ objectFit: 'cover', borderRadius: 8, border: '1px solid #f0f0f0' }}
        fallback="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="
      />
    ) : renderEmpty(fallbackText)
  );
  const renderDetailStatGrid = (items = []) => (
    <div className="detail-stat-grid">
      {items.map((item) => (
        <div className="detail-stat-card" key={item.label}>
          <div className="detail-stat-label">{item.label}</div>
          <div className="detail-stat-value" style={item.color ? { color: item.color } : undefined}>
            {item.value}
          </div>
        </div>
      ))}
    </div>
  );

  const loadPlayers = useCallback(async () => {
    setLoading(true);
    try {
      const filterValues = { ...filterForm.getFieldsValue(), server_id: serverScope };
      const params = buildPlayerListParams({ currentPage, pageSize, searchKeyword, sortConfig, filterValues });
      const response = await playerAPI.getList(params);
      setPlayers(response.data?.data?.players || []);
      setPagination(prev => ({
        ...prev,
        total: response.data?.data?.pagination?.total || 0
      }));
    } catch (error) {
      console.error('Load players error:', error);
      message.error('加载玩家列表失败：' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterForm, pageSize, searchKeyword, serverScope, sortConfig]);

  useEffect(() => {
    (async () => {
      try {
        const response = await authAPI.getZones();
        const rawZones = response.data?.data?.zones || [];
        const uniqueZones = new Map();
        rawZones.forEach((zone) => {
          const serverId = Number(zone.serverId);
          if (Number.isInteger(serverId) && serverId > 0 && !uniqueZones.has(serverId)) {
            uniqueZones.set(serverId, {
              serverId,
              label: zone.label || zone.name || `${serverId}区`
            });
          }
        });
        setZones([...uniqueZones.values()].sort((a, b) => a.serverId - b.serverId));
      } catch (error) {
        console.error('Load zones error:', error);
      }
    })();
  }, []);

  const handleSearch = (value = searchDraft) => {
    const nextKeyword = (value || '').trim();
    setSearchDraft(value || '');
    setSearchKeyword(nextKeyword);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  useEffect(() => {
    loadPlayers();
  }, [loadPlayers]);

  const handleReset = () => {
    filterForm.resetFields();
    setSearchDraft('');
    setSearchKeyword('');
    setServerScope('all');
    setSortConfig({ field: '', order: null });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleViewDetail = async (record, initialTab = 'basic') => {
    const playerId = record.game_id || record.id;
    if (!record.hasCharacter || !playerId) {
      message.warning('该账号暂无角色，无法查看角色详情');
      return;
    }

    setDetailLoading(true);
    try {
      const response = await playerAPI.getDetail(playerId, record.server_id);
      setCurrentPlayer({
        ...record,
        ...response.data.data.player,
        id: response.data.data.player.id || playerId
      });
      setPlayerResources(response.data.data.player.resources || null);
      const billing = response.data.data.player.billing || { available: false };
      setRechargeStats(billing);
      setRechargeLogs(billing.orders || []);
      setBillingPagination(billing.pagination || { page: 1, limit: 20, total: 0 });
      setBackpackItems(response.data.data.backpackItems || []);
      setBackpackKeyword('');
      setBackpackCategory('');
      setDetailVisible(true);
      setActiveTab(initialTab);
    } catch (error) {
      console.error('Load player detail error:', error);
      message.error('加载玩家详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleBan = (record) => {
    setCurrentPlayer({ id: record.game_id || record.id, server_id: record.server_id });
    setBanVisible(true);
  };

  const handleBanSubmit = async (values) => {
    try {
      await playerAPI.ban(currentPlayer.id, values, currentPlayer.server_id);
      message.success('封禁玩家成功');
      setBanVisible(false);
      banForm.resetFields();
      loadPlayers();
    } catch (error) {
      message.error('封禁玩家失败');
    }
  };

  const handleUnban = async (record) => {
    try {
      await playerAPI.unban(record.game_id || record.id, {}, record.server_id);
      message.success('解封玩家成功');
      loadPlayers();
    } catch (error) {
      message.error('解封玩家失败');
    }
  };

  const handleRecharge = (record) => {
    const playerId = record.game_id || record.id;
    if (!playerId || !record.account_id) {
      message.warning('该角色缺少后台账号关联，需先核实归属，不能直接充值');
      return;
    }
    const player = { id: playerId, server_id: record.server_id };
    try {
      const saved = JSON.parse(localStorage.getItem(rechargeStorageKey(player)) || 'null');
      rechargeForm.resetFields();
      rechargeForm.setFieldsValue(saved?.values || { accountingMode: 'gift', rechargeAmount: 0 });
      setAccountingMode(saved?.values?.accountingMode || 'gift');
      setUseIntegral(Boolean(saved?.withIntegral));
      setPendingRecharge(Boolean(saved));
      setCurrentPlayer(player);
      setRechargeVisible(true);
      loadIntegralConfig();
    } catch (error) {
      message.error('无法读取待处理记录，请检查浏览器存储；为防重复发放，本次未提交');
    }
  };
  const handleViewSafeCode = async (record) => {
    const playerId = record.game_id || record.id;
    if (!record.hasCharacter || !playerId) {
      message.warning('该账号暂无角色，无法查看安全码');
      return;
    }

    setSafeCodeLoading(true);
    setSafeCodeVisible(true);
    try {
      const response = await playerAPI.getSafeCode(playerId, record.server_id);
      setSafeCodeInfo({
        playerId,
        playerName: record.nickname || record.name || '',
        ...response.data?.data
      });
    } catch (error) {
      console.error('Load player safe code error:', error);
      setSafeCodeInfo(null);
      message.error('加载玩家安全码失败');
    } finally {
      setSafeCodeLoading(false);
    }
  };

  const loadIntegralConfig = async () => {
    try {
      const response = await playerAPI.getIntegralConfig();
      if (response.data.code === 200) {
        setIntegralConfig(response.data.data);
        integralConfigForm.setFieldsValue({
          integral_rate: response.data.data.integral_rate,
          integral_global_type_id: response.data.data.integral_global_type_id
        });
      }
    } catch (error) {
      console.error('Load integral config error:', error);
    }
  };

  const handleIntegralConfigSubmit = async (values) => {
    try {
      await playerAPI.updateIntegralConfig(values);
      message.success('充值积分转化率更新成功');
      setIntegralConfigVisible(false);
      loadIntegralConfig();
    } catch (error) {
      message.error('更新失败：' + (error.response?.data?.message || error.message));
    }
  };

  const handleRechargeSubmit = async (values) => {
    if (rechargeBusy.current) return;
    rechargeBusy.current = true;
    setRechargeSubmitting(true);
    const storageKey = rechargeStorageKey(currentPlayer);
    try {
      let saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (!saved) {
        const random = Array.from(crypto.getRandomValues(new Uint8Array(16)), b => b.toString(16).padStart(2, '0')).join('');
        saved = { requestId: random, values, withIntegral: useIntegral };
        localStorage.setItem(storageKey, JSON.stringify(saved));
      }
      setPendingRecharge(true);
      const payload = { ...saved.values, request_id: saved.requestId };
      const response = saved.withIntegral
        ? await playerAPI.rechargeWithIntegral(currentPlayer.id, payload, currentPlayer.server_id)
        : await playerAPI.recharge(currentPlayer.id, payload, currentPlayer.server_id);
      localStorage.removeItem(storageKey);
      message.success(`操作完成，订单：${response.data.data.order_no}`);
      setPendingRecharge(false);
      setRechargeVisible(false);
      rechargeForm.resetFields();
      setUseIntegral(false);
    } catch (error) {
      if ([400, 404].includes(error.response?.status)) {
        localStorage.removeItem(storageKey);
        setPendingRecharge(false);
      }
      message.error(error.response?.data?.message || '未能确认结果，请保留本次记录，勿另建重复操作');
    } finally {
      rechargeBusy.current = false;
      setRechargeSubmitting(false);
    }
  };

  const loadBillingPage = async (page, limit) => {
    setDetailLoading(true);
    try {
      const response = await playerAPI.getBilling(currentPlayer.id, { page, limit }, currentPlayer.server_id);
      const billing = response.data.data;
      setRechargeStats(billing);
      setRechargeLogs(billing.orders || []);
      setBillingPagination(billing.pagination);
    } catch (error) {
      message.error('加载充值记录失败，已保留原有记录');
    } finally { setDetailLoading(false); }
  };

  const handleSendMail = (record) => {
    const playerId = record.game_id || record.id;
    if (!playerId) {
      message.warning('该账号暂无角色，无法发送邮件');
      return;
    }
    setCurrentPlayer({ id: playerId, server_id: record.server_id });
    setMailVisible(true);
  };

  const handleSendMailSubmit = async (values) => {
    try {
      await playerAPI.sendMail(currentPlayer.id, values, currentPlayer.server_id);
      message.success('发送邮件成功');
      setMailVisible(false);
      mailForm.resetFields();
    } catch (error) {
      message.error('发送邮件失败');
    }
  };

  const handleResetPassword = (record) => {
    const accountId = Number(record?.account_id || 0);
    if (!Number.isInteger(accountId) || accountId <= 0) {
      message.warning('该行缺少账号ID，无法修改密码');
      return;
    }

    setCurrentPlayer({ ...record, id: accountId });
    setResetPasswordVisible(true);
    resetPasswordForm.resetFields();
  };

  const handleResetPasswordSubmit = async (values) => {
    try {
      const response = await playerAPI.resetPassword(currentPlayer.id, { newPassword: values.newPassword });
      const updatedCount = Number(response.data?.data?.updated_count || 0);
      message.success(updatedCount > 1 ? `密码修改成功，已同步 ${updatedCount} 个关联账号` : '密码修改成功');
      setResetPasswordVisible(false);
      resetPasswordForm.resetFields();
    } catch (error) {
      message.error(error.response?.data?.message || '密码修改失败');
    }
  };

  const handleBatchBan = async () => {
    try {
      for (const record of selectedRows) {
        const playerId = record.game_id || record.id;
        await playerAPI.ban(playerId, { reason: '批量封禁', duration: 24 }, record.server_id);
      }
      message.success(`批量封禁 ${selectedRows.length} 个玩家成功`);
      setSelectedRowKeys([]);
      setSelectedRows([]);
      loadPlayers();
    } catch (error) {
      message.error('批量封禁失败');
    }
  };

  const resetPlayerDetailState = useCallback(() => {
    setCurrentPlayer(null);
    setPlayerResources(null);
    setRechargeStats(null);
    setRechargeLogs([]);
    setBackpackItems([]);
    setBackpackKeyword('');
    setBackpackCategory('');
  }, []);

  const columns = [
    {
      title: '账号',
      
      dataIndex: 'account_nick_name',
      key: 'account_nick_name',
      width: 180,
      fixed: 'left',
      render: (text, record) => renderTableStack(
        text || record.user_name || '未命名账号',
        record.account_id ? `账号ID ${record.account_id}` : '未关联后台账号（待核实）'
      )
    },
    {
      title: '玩家 ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      fixed: 'left',
      sorter: true,
      sortOrder: sortConfig.field === 'id' ? sortConfig.order : null,
      render: (text, record) => renderTableStack(
        <span className="table-cell-mono">{text}</span>,
        record.game_id ? `角色ID ${record.game_id}` : '无角色ID'
      )
    },
    {
      title: '所属代理',
      dataIndex: 'agent_name',
      key: 'agent_name',
      width: 150,
      render: (text, record) => text
        ? renderTableStack(
          <Tag color="geekblue">{text}</Tag>,
          record.invite_code ? `邀请码 ${record.invite_code}` : (record.agent_id ? `代理ID ${record.agent_id}` : '')
        )
        : renderEmpty('未绑定代理')
    },
    {
      title: '游戏昵称',
      dataIndex: 'nickname',
      key: 'nickname',
      width: 150,
      render: (text, record) => {
        const charName = text || record.name || '未设置';
        const status = record.hasCharacter ? '当前账号存在角色' : '当前账号暂无角色';
        return renderTableStack(charName, status);
      }
    },

    {
      title: '服务器',
      dataIndex: 'server_id',
      key: 'server_id',
      width: 100,
      sorter: true,
      sortOrder: sortConfig.field === 'server_id' ? sortConfig.order : null,
      render: (text, record) => record.hasCharacter
        ? renderTableStack(<Tag color="processing">S{text}</Tag>, '当前角色所在区服')
        : <Tag>无角色</Tag>
    },
    {
      title: '等级',
      dataIndex: 'level',
      key: 'level',
      width: 80,
      sorter: true,
      sortOrder: sortConfig.field === 'level' ? sortConfig.order : null,
      render: (text, record) => record.hasCharacter
        ? renderTableStack(<Tag color="orange">Lv.{text ?? 0}</Tag>, '角色当前等级')
        : renderEmpty('无角色')
    },
    {
      title: 'VIP',
      dataIndex: 'vip_lv',
      key: 'vip_lv',
      width: 80,
      sorter: true,
      sortOrder: sortConfig.field === 'vip_lv' ? sortConfig.order : null,
      render: (text, record) => {
        if (!record.hasCharacter) {
          return renderEmpty('无角色');
        }
        return renderTableStack(text > 0 ? <Tag color="gold">VIP{text}</Tag> : <Tag>VIP0</Tag>, '泡点与充值相关数据可在详情中查看');
      }
    },
    {
      title: '战斗力',
      dataIndex: 'power',
      key: 'power',
      width: 100,
      sorter: true,
      sortOrder: sortConfig.field === 'power' ? sortConfig.order : null,
      render: (text, record) => record.hasCharacter
        ? renderTableStack(formatNumber(text, '0'), '战斗力')
        : renderEmpty('无角色')
    },
    {
      title: '注册时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 160,
      sorter: true,
      sortOrder: sortConfig.field === 'create_time' ? sortConfig.order : null,
      render: (text, record) => renderTableStack(
        record.hasCharacter ? formatDateTime(text, { seconds: true }) : formatDateTime(record.account_create_time),
        record.hasCharacter ? '角色创建时间' : '账号创建时间'
      )
    },
    {
      title: '最后活跃',
      dataIndex: 'last_login_time',
      key: 'last_login_time',
      width: 160,
      render: (text, record) => record.hasCharacter
        ? renderTableStack(
          formatDateTime(text, { seconds: true, fallback: '从未登录' }),
          text ? '最近活跃时间' : '暂无登录记录'
        )
        : renderEmpty('无角色')
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      sorter: true,
      sortOrder: sortConfig.field === 'status' ? sortConfig.order : null,
      render: (status) => (
        <Tag color={status == null ? 'default' : status === 1 ? 'green' : 'red'}>
          {status == null ? '账号未关联' : status === 1 ? '正常' : '已封禁'}
        </Tag>
      )
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 410,
      render: (_, record) => (
        <Space direction="vertical" size={8}>
          <div className="action-group">
            <Tooltip title={record.hasCharacter ? '查看详情' : '该账号暂无角色'}>
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => handleViewDetail(record)}
                size="small"
                disabled={!record.hasCharacter || !record.game_id}
              >
                详情
              </Button>
            </Tooltip>
            <Tooltip title={record.hasCharacter ? '充值' : '该账号暂无角色'}>
              <Button
                type="link"
                icon={<DollarOutlined />}
                onClick={() => handleRecharge(record)}
                size="small"
                disabled={!record.hasCharacter || !record.game_id || !record.account_id}
              >
                充值
              </Button>
            </Tooltip>
            <Tooltip title={record.hasCharacter ? '直接查看充值明细' : '该账号暂无角色'}>
              <Button
                type="link"
                icon={<DollarOutlined />}
                onClick={() => handleViewDetail(record, 'recharge')}
                size="small"
                disabled={!record.hasCharacter || !record.game_id}
              >
                充值明细
              </Button>
            </Tooltip>
            <Tooltip title={record.hasCharacter ? '发送邮件' : '该账号暂无角色'}>
              <Button
                type="link"
                icon={<MailOutlined />}
                onClick={() => handleSendMail(record)}
                size="small"
                disabled={!record.hasCharacter || !record.game_id}
              >
                邮件
              </Button>
            </Tooltip>
            <Tooltip title="修改密码">
              <Button
                type="link"
                icon={<KeyOutlined />}
                onClick={() => handleResetPassword(record)}
                size="small"
                disabled={!record.account_id}
              >
                改密
              </Button>
            </Tooltip>
            <Tooltip title={record.hasCharacter ? '查看安全码' : '该账号暂无角色'}>
              <Button
                type="link"
                icon={<InfoCircleOutlined />}
                onClick={() => handleViewSafeCode(record)}
                size="small"
                disabled={!record.hasCharacter || !record.game_id}
              >
                安全码
              </Button>
            </Tooltip>
          </div>
          <div className="action-group">
            {record.status === 1 ? (
              <Popconfirm
                title="确定要封禁此玩家吗？"
                onConfirm={() => handleBan(record)}
                okText="确定"
                cancelText="取消"
              >
                <Button type="link" danger icon={<StopOutlined />} size="small">
                  封禁
                </Button>
              </Popconfirm>
            ) : (
              <Popconfirm
                title="确定要解封此玩家吗？"
                onConfirm={() => handleUnban(record)}
                okText="确定"
                cancelText="取消"
              >
                <Button type="link" icon={<CheckCircleOutlined />} size="small" disabled={record.status == null}>
                  解封
                </Button>
              </Popconfirm>
            )}
          </div>
        </Space>
      )
    }
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys, rows) => {
      setSelectedRowKeys(selectedKeys);
      setSelectedRows(rows);
    }
  };

  const hasFilters = Boolean(
    searchKeyword ||
    filterForm.getFieldValue('status') !== undefined ||
    serverScope !== 'all' ||
    filterForm.getFieldValue('dateRange')
  );

  return (
    <div className="page-shell">
      <Card
        className="filter-card player-filter-card"
        bordered={false}
      >
        <div className="player-filter-shell">
          <div className="player-filter-top">
            <div className="player-filter-heading">
              <div className="player-filter-title-row">
                <div className="player-filter-title">玩家检索</div>
                <div className="player-filter-tags">
                  <Tag color="blue">当前列表 {players.length} 项</Tag>
                  <Tag color="green">仅显示有角色账号</Tag>
                  {hasFilters && <Tag color="gold">已启用筛选</Tag>}
                </div>
              </div>
              <div className="player-filter-subtitle">
                搜索支持玩家 ID、用户名和昵称，常用筛选和批量操作统一放在这里。
              </div>
            </div>
          </div>
          <div className="player-command-bar">
            <div className="player-command-main">
              <Select
                value={serverScope}
                onChange={(value) => {
                  setServerScope(value || 'all');
                  setPagination(prev => ({ ...prev, current: 1 }));
                }}
                style={{ width: 190 }}
                size="large"
                showSearch
                optionFilterProp="children"
              >
                <Option value="all">全服玩家</Option>
                {zones.map((zone) => (
                  <Option key={zone.serverId} value={zone.serverId}>
                    {zone.label}（{zone.serverId}区）
                  </Option>
                ))}
              </Select>
              <div className="player-command-input-shell">
                <span className="player-command-input-icon">
                  <SearchOutlined />
                </span>
                <Input
                  value={searchDraft}
                  onChange={(event) => setSearchDraft(event.target.value)}
                  onPressEnter={() => handleSearch()}
                  allowClear
                  bordered={false}
                  className="player-command-input"
                  placeholder="输入玩家 ID、用户名或昵称后直接查询"
                />
              </div>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={() => handleSearch()}
                className="player-command-search-button"
              >
                查询玩家
              </Button>
            </div>
            <div className="player-command-secondary">
              <Space wrap>
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => setAdvancedSearchVisible(!advancedSearchVisible)}
                  size="large"
                >
                  {advancedSearchVisible ? '收起筛选' : '高级筛选'}
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleReset}
                  size="large"
                >
                  刷新
                </Button>
              </Space>
            </div>
          </div>
          <div className="player-filter-summary">
            <Text type="secondary">当前总记录 {pagination.total || 0}</Text>
            {selectedRowKeys.length > 0 ? (
              <Space wrap>
                <Text type="secondary">已选择 {selectedRowKeys.length} 项</Text>
                <Popconfirm
                  title={`确定要封禁 ${selectedRowKeys.length} 个玩家吗？`}
                  onConfirm={handleBatchBan}
                  okText="确定"
                  cancelText="取消"
                >
                  <Button danger icon={<StopOutlined />}>
                    批量封禁
                  </Button>
                </Popconfirm>
              </Space>
            ) : (
              <Text type="secondary">未选择任何记录</Text>
            )}
          </div>

          {advancedSearchVisible && (
            <Card
              size="small"
              type="inner"
              title="高级筛选"
              className="detail-card"
              bodyStyle={{ paddingBottom: 8 }}
            >
              <Form form={filterForm} layout="vertical">
                <Row gutter={[16, 0]}>
                  <Col xs={24} md={8} xl={6}>
                    <Form.Item name="status" label="状态">
                      <Select placeholder="请选择状态" allowClear>
                        <Option value={1}>正常</Option>
                        <Option value={0}>已封禁</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8} xl={6}>
                    <Form.Item label="服务器">
                      <Select value={serverScope} onChange={(value) => setServerScope(value || 'all')} showSearch optionFilterProp="children">
                        <Option value="all">全服玩家</Option>
                        {zones.map((zone) => (
                          <Option key={zone.serverId} value={zone.serverId}>
                            {zone.label}（服务器ID：{zone.serverId}）
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8} xl={8}>
                    <Form.Item name="dateRange" label="注册时间">
                      <RangePicker style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} xl={4}>
                    <Form.Item label=" ">
                      <Space wrap>
                        <Button type="primary" onClick={loadPlayers} icon={<SearchOutlined />}>
                          查询
                        </Button>
                        <Button onClick={handleReset}>
                          清空
                        </Button>
                      </Space>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Card>
          )}
        </div>
      </Card>

      <Card
        title="玩家列表"
        className="table-card"
        bordered={false}
        extra={<Text type="secondary">{hasFilters ? '当前为筛选结果' : '当前为全量结果'}，共 {pagination.total || 0} 条</Text>}
      >
        <Table
          columns={columns}
          dataSource={players}
          rowKey={(record) => `${record.server_id || 0}:${record.game_id || record.id}`}
          loading={loading}
          locale={{ emptyText: renderEmptyPanel('暂无玩家数据', '请调整搜索词或筛选条件后重试。') }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`
          }}
          onChange={(pag, _, sorter) => {
            setPagination(prev => ({ ...prev, current: pag.current, pageSize: pag.pageSize }));
            if (Array.isArray(sorter)) {
              return;
            }
            setSortConfig({
              field: sorter?.field || '',
              order: sorter?.order || null
            });
          }}
          scroll={{ x: 1780 }}
          rowSelection={rowSelection}
          size="middle"
        />
      </Card>

      {/* 玩家详情抽屉 */}
      <Drawer
        title="玩家详情"
        placement="right"
        open={detailVisible}
        onClose={() => {
          setDetailVisible(false);
          resetPlayerDetailState();
        }}
        width={800}
      >
        {detailLoading ? (
          <div className="drawer-loading">
            <Spin tip="正在加载玩家详情..." />
          </div>
        ) : currentPlayer && (
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <Tabs.TabPane tab="基本信息" key="basic">
              <div className="detail-section">
                <div className="section-note">
                  <div className="section-note-text">
                    <span className="section-note-strong">
                      {currentPlayer.nickname || currentPlayer.name || currentPlayer.user_name || '当前玩家'}
                    </span>
                    {currentPlayer.hasCharacter ? ' 已同步角色资料，可继续查看资源、背包和充值流水。' : ' 当前账号暂无角色资料。'}
                  </div>
                  <Tag color={currentPlayer.status == null ? 'default' : currentPlayer.status === 1 ? 'green' : 'red'}>
                    {currentPlayer.status == null ? '账号状态未关联' : currentPlayer.status === 1 ? '状态正常' : '已封禁'}
                  </Tag>
                </div>

                <Descriptions column={2} bordered size="small">
                <Descriptions.Item label="账号 ID">{currentPlayer.account_id || renderEmpty()}</Descriptions.Item>
                <Descriptions.Item label="玩家 ID">{currentPlayer.id || renderEmpty()}</Descriptions.Item>
                <Descriptions.Item label="账号昵称">{currentPlayer.account_nick_name || currentPlayer.user_name || currentPlayer.username || renderEmpty()}</Descriptions.Item>
                <Descriptions.Item label="游戏昵称">{currentPlayer.nickname || currentPlayer.name || renderEmpty()}</Descriptions.Item>
                <Descriptions.Item label="角色状态">
                  <Badge
                    status={currentPlayer.hasCharacter ? 'success' : 'default'}
                    text={currentPlayer.hasCharacter ? '已有角色' : '暂无角色'}
                  />
                </Descriptions.Item>
                <Descriptions.Item label="服务器 ID">{currentPlayer.server_id ?? renderEmpty()}</Descriptions.Item>
                <Descriptions.Item label="等级">{currentPlayer.level ?? renderEmpty()}</Descriptions.Item>
                <Descriptions.Item label="VIP">{currentPlayer.vip_lv ?? renderEmpty()}</Descriptions.Item>
                <Descriptions.Item label="平台币余额">
                  <Text strong style={{ color: '#d97706' }}>
                    {Number(currentPlayer.platformCoinBalance || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="战斗力">{currentPlayer.hasCharacter ? formatNumber(currentPlayer.power, '0') : renderEmpty('暂无角色')}</Descriptions.Item>
                <Descriptions.Item label="状态">
                  <Tag color={currentPlayer.status == null ? 'default' : currentPlayer.status === 1 ? 'green' : 'red'}>
                    {currentPlayer.status == null ? '账号状态未关联' : currentPlayer.status === 1 ? '正常' : '已封禁'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="账号注册时间">
                  {formatDateTime(currentPlayer.account_create_time)}
                </Descriptions.Item>
                <Descriptions.Item label="角色创建时间">
                  {formatDateTime(currentPlayer.create_time, { seconds: true, fallback: '暂无角色' })}
                </Descriptions.Item>
                <Descriptions.Item label="最后活跃">
                  {formatDateTime(currentPlayer.last_login_time, { seconds: true, fallback: '从未登录' })}
                </Descriptions.Item>
                <Descriptions.Item label="当前头像" span={2}>
                  <Space size={16} align="center" wrap>
                    {renderAvatarPreview(currentPlayer.custom_icon_url, '无自定义头像')}
                    <Space direction="vertical" size={2}>
                      <Text>当前头像ID：{currentPlayer.custom_icon || EMPTY_TEXT}</Text>
                      <Text type="secondary">
                        访问地址：{currentPlayer.custom_icon_url || '未生成'}
                      </Text>
                    </Space>
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="待审头像" span={2}>
                  <Space size={16} align="center" wrap>
                    {renderAvatarPreview(currentPlayer.approve_icon_url, '当前无待审头像')}
                    <Space direction="vertical" size={2}>
                      <Text>待审中：{currentPlayer.need_approve ? '是' : '否'}</Text>
                      <Text>待审头像ID：{currentPlayer.approve_icon || EMPTY_TEXT}</Text>
                      <Text type="secondary">
                        访问地址：{currentPlayer.approve_icon_url || '未生成'}
                      </Text>
                    </Space>
                  </Space>
                </Descriptions.Item>
                </Descriptions>
              </div>
            </Tabs.TabPane>

            <Tabs.TabPane tab="资源信息" key="resources">
              {currentPlayer.hasCharacter && playerResources ? (
                <div className="detail-section">
                  <div className="detail-section-head">
                    <div>
                      <div className="detail-section-title">资源概览</div>
                      <div className="detail-section-subtitle">统一展示角色常用资源，方便和充值、背包信息对照查看。</div>
                    </div>
                  </div>
                  {renderDetailStatGrid([
                    { label: '平台币余额', value: Number(currentPlayer.platformCoinBalance || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), color: '#d97706' },
                    { label: '体力', value: formatNumber(playerResources.stamina || 0, '0') },
                    { label: '粮食', value: formatNumber(playerResources.food || 0, '0') },
                    { label: '石油', value: formatNumber(playerResources.oil || 0, '0') },
                    { label: '钢铁', value: formatNumber(playerResources.iron || 0, '0') },
                    { label: '稀土', value: formatNumber(playerResources.rare_earth || playerResources.rareEarth || 0, '0') },
                    { label: 'AOZ', value: formatNumber(playerResources.aoz_coin || playerResources.flame || 0, '0'), color: '#7c3aed' },
                    { label: '免费 AOZ', value: formatNumber(playerResources.free_aoz_coin || playerResources.free_flame || 0, '0'), color: '#16a34a' }
                  ])}
                </div>
              ) : (
                renderEmptyPanel(
                  currentPlayer.hasCharacter ? '暂无资源信息' : '该账号暂无角色资源信息',
                  '后端未返回资源快照时，这里会保持空态显示。'
                )
              )}
            </Tabs.TabPane>

            <Tabs.TabPane
              tab={`背包物品${backpackItems.length > 0 ? ` (${backpackItems.length})` : ''}`}
              key="backpack"
            >
              {!currentPlayer.hasCharacter ? (
                renderEmptyPanel('该账号暂无角色背包信息', '当前账号还没有可查看的角色背包数据。')
              ) : backpackItems.length === 0 ? (
                renderEmptyPanel('该玩家背包暂无可显示物品', '如果刚创建角色或暂未同步背包，这里会显示为空。')
              ) : (
                <div className="detail-section">
                  {renderDetailStatGrid([
                    { label: '筛后种类', value: filteredBackpackSummary.total_types },
                    { label: '筛后总数量', value: formatNumber(filteredBackpackSummary.total_count, '0') },
                    { label: '筛后限时物品', value: filteredBackpackSummary.expiring_types, color: '#ea580c' }
                  ])}

                  <div className="section-note">
                    <div className="section-note-text">
                      <span className="section-note-strong">背包筛选只影响当前抽屉视图。</span>
                      支持按物品名、物品 ID、说明和分类快速缩小范围。
                    </div>
                    <div className="table-tag-row">
                      <Tag color="blue">原始 {backpackItems.length} 种</Tag>
                      <Tag color="green">筛后 {filteredBackpackItems.length} 种</Tag>
                      {backpackCategory && <Tag color={getCategoryColor(backpackCategory)}>{backpackCategory}</Tag>}
                    </div>
                  </div>

                  <Row gutter={[12, 12]}>
                    <Col xs={24} md={14}>
                      <Search
                        allowClear
                        value={backpackKeyword}
                        onChange={(e) => setBackpackKeyword(e.target.value)}
                        placeholder="搜索物品名、物品ID或说明"
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <Select
                        allowClear
                        value={backpackCategory || undefined}
                        onChange={(value) => setBackpackCategory(value || '')}
                        placeholder="筛选物品分类"
                        style={{ width: '100%' }}
                      >
                        {backpackCategories.map((category) => (
                          <Option key={category} value={category}>{category}</Option>
                        ))}
                      </Select>
                    </Col>
                    <Col xs={24} md={2}>
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={() => {
                          setBackpackKeyword('');
                          setBackpackCategory('');
                        }}
                      />
                    </Col>
                  </Row>

                  <Table
                    dataSource={filteredBackpackItems}
                    rowKey="id"
                    size="small"
                    pagination={{
                      pageSize: 20,
                      showSizeChanger: true,
                      pageSizeOptions: ['20', '50', '100'],
                      showTotal: (total) => `共 ${total} 种`
                    }}
                    scroll={{ y: 420, x: 980 }}
                    locale={{ emptyText: renderEmptyPanel('没有符合筛选条件的背包物品', '请尝试清空关键字或切换物品分类。') }}
                  >
                    <Table.Column
                      title="物品ID"
                      dataIndex="gid"
                      width={110}
                      render={(value) => <Text copyable={{ text: String(value) }}>{value}</Text>}
                    />
                    <Table.Column
                      title="物品名称"
                      dataIndex="item_name"
                      width={180}
                      render={(value) => <Text strong>{value || '未收录道具'}</Text>}
                    />
                    <Table.Column
                      title="分类"
                      dataIndex="category"
                      width={110}
                      render={(value) => <Tag color={getCategoryColor(value)}>{value || '未分类'}</Tag>}
                    />
                    <Table.Column
                      title="等级"
                      dataIndex="level"
                      width={80}
                      render={(value) => value > 0 ? <Tag color="processing">Lv.{value}</Tag> : '-'}
                    />
                    <Table.Column
                      title="品质"
                      dataIndex="quality"
                      width={90}
                      render={(value) => (
                        value
                          ? <Tag color={getQualityColor(value)}>{getQualityText(value)}</Tag>
                          : renderEmpty('未知')
                      )}
                    />
                    <Table.Column
                      title="数量"
                      dataIndex="count"
                      width={100}
                      sorter={(a, b) => (a.count || 0) - (b.count || 0)}
                      defaultSortOrder="descend"
                      render={(value) => <Text strong>{formatNumber(value, '0')}</Text>}
                    />
                    <Table.Column
                      title="到期时间"
                      dataIndex="expire_time"
                      width={180}
                      render={(value, record) => {
                        if (!value) {
                          return <Tag>永久</Tag>;
                        }
                        return (
                          <Space direction="vertical" size={0}>
                            <span>{formatDateTime(value, { seconds: true, fallback: '-' })}</span>
                            <Tag color={record.is_expired ? 'red' : 'gold'}>
                              {record.is_expired ? '已过期' : '限时'}
                            </Tag>
                          </Space>
                        );
                      }}
                    />
                    <Table.Column
                      title="说明"
                      dataIndex="description"
                      ellipsis={{ showTitle: true }}
                      render={(value) => value || renderEmpty('无说明')}
                    />
                  </Table>
                </div>
              )}
            </Tabs.TabPane>

            <Tabs.TabPane tab="充值记录" key="recharge">
              <div className="detail-section">
                <Alert type={rechargeStats?.available ? 'info' : 'error'} showIcon className="block-gap-md"
                  message={rechargeStats?.available ? (rechargeStats.account_linked ? '关联账号全区统计：现金实收与有效累充分开；平台币消费、运营赠币不重复计现金。' : '角色未关联后台账号：仅展示已确认角色的订单，未自动绑定账号。') : '充值统计暂不可用，不能按零充值处理，请稍后重试。'} />
                {rechargeStats?.available && renderDetailStatGrid([
                  { label: '今日有效累充', value: formatRawFlowCurrency(rechargeStats.today), color: '#16a34a' },
                  { label: '昨日有效累充', value: formatRawFlowCurrency(rechargeStats.yesterday), color: '#2563eb' },
                  { label: '近7日有效累充', value: formatRawFlowCurrency(rechargeStats.week7), color: '#7c3aed' },
                  { label: '账号有效累充（全区）', value: formatRawFlowCurrency(rechargeStats.total), color: '#ea580c' },
                  { label: '账号现金实收（全区）', value: formatRawFlowCurrency(rechargeStats.cash_total), color: '#16a34a' }
                ])}
                <Table dataSource={rechargeLogs} rowKey="id" size="small" loading={detailLoading}
                  pagination={{ current: billingPagination.page, pageSize: billingPagination.limit, total: billingPagination.total, showSizeChanger: true, onChange: loadBillingPage }}
                  scroll={{ x: 1000, y: 400 }} locale={{ emptyText: rechargeStats?.available ? '关联账号暂无订单记录' : '充值记录暂不可用' }}>
                  <Table.Column title="订单号" dataIndex="order_no" width={210} />
                  <Table.Column title="区服 / 游戏UID" render={(_, r) => `${r.server_id}区 / ${r.character_id}`} width={150} />
                  <Table.Column title="商品 / 操作" dataIndex="product_name" />
                  <Table.Column title="金额" dataIndex="total_amount" width={100} render={formatRawFlowCurrency} />
                  <Table.Column title="统计口径" width={160} render={(_, r) => <Space direction="vertical" size={0}>
                    {Number(r.counts_cash) > 0 && <Tag color="green">计现金实收</Tag>}
                    {Number(r.counts_cumulative) > 0 && <Tag color="blue">计有效累充</Tag>}
                    {!Number(r.counts_cash) && !Number(r.counts_cumulative) && <Tag>不计充值</Tag>}
                  </Space>} />
                  <Table.Column title="状态" dataIndex="status" width={115} render={(v, r) => <Tag>{r.product_type === 'admin_direct_grant' && Number(v) === 2 ? '处理中/待核实' : ({ 0: '待付款', 1: '已付款', 2: '发放中', 3: '已完成', 4: '已取消' }[v] || '未知')}</Tag>} />
                  <Table.Column title="创建时间" dataIndex="create_time" width={180} render={v => v ? new Date(v).toLocaleString('zh-CN') : '-'} />
                </Table>
              </div>
            </Tabs.TabPane>
          </Tabs>
        )}
      </Drawer>

      <Modal
        title="玩家安全码"
        open={safeCodeVisible}
        onCancel={() => {
          setSafeCodeVisible(false);
          setSafeCodeInfo(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setSafeCodeVisible(false);
              setSafeCodeInfo(null);
            }}
          >
            关闭
          </Button>
        ]}
      >
        {safeCodeLoading ? (
          <div className="modal-loading">
            <Spin tip="正在加载安全码..." />
          </div>
        ) : safeCodeInfo ? (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="玩家 ID">
              {safeCodeInfo?.player_id ?? safeCodeInfo?.playerId ?? renderEmpty()}
            </Descriptions.Item>
            <Descriptions.Item label="玩家名称">
              {safeCodeInfo?.player_name || safeCodeInfo?.playerName || renderEmpty()}
            </Descriptions.Item>
            <Descriptions.Item label="安全码状态">
              <Tag color={safeCodeInfo?.use_safe_code ? 'green' : 'default'}>
                {safeCodeInfo?.use_safe_code ? '已启用' : '未启用'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="安全码 ID">
              {safeCodeInfo?.safe_code_id ? safeCodeInfo.safe_code_id : renderEmpty('未设置')}
            </Descriptions.Item>
            <Descriptions.Item label="安全码">
              {safeCodeInfo?.safe_code ? (
                <Text copyable>{safeCodeInfo.safe_code}</Text>
              ) : renderEmpty('未设置')}
            </Descriptions.Item>
            <Descriptions.Item label="登录码">
              {safeCodeInfo?.login_code ? (
                <Text copyable>{safeCodeInfo.login_code}</Text>
              ) : renderEmpty('未设置')}
            </Descriptions.Item>
            <Descriptions.Item label="安全设备">
              {safeCodeInfo?.safe_device || renderEmpty('未设置')}
            </Descriptions.Item>
          </Descriptions>
        ) : (
          renderEmptyPanel('暂未获取到安全码信息', '请确认该角色已启用安全码或稍后重试。')
        )}
      </Modal>

      {/* 封禁玩家弹窗 */}
      <Modal
        title="封禁玩家"
        open={banVisible}
        onCancel={() => {
          setBanVisible(false);
          banForm.resetFields();
        }}
        onOk={() => banForm.submit()}
      >
        <Form form={banForm} layout="vertical" onFinish={handleBanSubmit}>
          <div className="page-note page-note-flat">
            <div className="page-note-text">
              <span className="page-note-strong">封禁会直接影响账号与角色使用。</span>
              建议先确认违规原因和封禁时长，再执行操作，避免误封。
            </div>
          </div>
          <Form.Item
            name="reason"
            label="封禁原因"
            rules={[{ required: true, message: '请输入封禁原因' }]}
          >
            <TextArea rows={4} placeholder="请输入封禁原因" />
          </Form.Item>
          <Form.Item
            name="duration"
            label="封禁时长（小时）"
            rules={[{ required: true, message: '请输入封禁时长' }]}
          >
            <InputNumber min={1} placeholder="请输入封禁时长（小时）" className="field-width-full" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 充值弹窗 */}
      <Modal
        title="玩家充值 / 运营赠币"
        open={rechargeVisible}
        confirmLoading={rechargeSubmitting}
        okText={pendingRecharge ? '核实上次操作（不会重复发币）' : '确认提交并留单'}
        onCancel={() => { if (!rechargeBusy.current) setRechargeVisible(false); }}
        onOk={() => rechargeForm.submit()}
        width={600}
      >
        <Form form={rechargeForm} layout="vertical" onFinish={handleRechargeSubmit} disabled={pendingRecharge || rechargeSubmitting}>
          <Alert className="block-gap-md" showIcon type={pendingRecharge ? 'warning' : 'info'}
            message={pendingRecharge ? '有尚未确认的操作，已保留原请求；核实按钮只查询/续用原请求，不会重复发币。请勿清除浏览器记录或换账号重复提交。' : '后台操作会留存订单。赠币不计充值；计累充需确认收款或调整依据，不计入现金实收。'} />
          <Form.Item name="accountingMode" label="统计方式" rules={[{ required: true }]}>
            <Select onChange={value => { setAccountingMode(value); if (value === 'gift') { setUseIntegral(false); rechargeForm.setFieldValue('rechargeAmount', 0); } }}
              options={[{ value: 'gift', label: '运营赠币（不计现金、不计累充）' }, { value: 'cumulative', label: '人工确认充值（计有效累充，不计现金实收）' }]} />
          </Form.Item>
          <Form.Item name="remark" label="操作原因 / 收款凭证编号" rules={[{ required: true, whitespace: true, message: '请填写操作依据' }, { max: 255 }]}>
            <Input.TextArea rows={2} maxLength={255} placeholder="请填写可核查的依据，避免同一笔充值重复记账" />
          </Form.Item>
          <div className="highlight-panel block-gap-md">
            <div className="table-cell-primary">充值与积分联动</div>
            <div className="table-cell-secondary">金额、AOZ 和积分配置统一放在同一弹窗，避免多次切换。</div>
          </div>
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="rechargeAmount"
                label="确认计入有效累充的金额（元）"
                rules={[{ required: accountingMode === 'cumulative', type: 'number', min: accountingMode === 'cumulative' ? 0.01 : 0, max: 1000000, message: '请填写有效金额' }]}
              >
                <InputNumber
                  min={0}
                  precision={2}
                  className="field-width-full"
                  disabled={accountingMode === 'gift' || pendingRecharge || rechargeSubmitting}
                  placeholder="请输入充值金额"
                  addonBefore="¥"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <div className="block-top-lg">
                <Space direction="vertical" size="small" className="space-full">
                  <div className="row-between-center">
                    <span className="text-size-md">充值积分</span>
                    <Switch
                      checked={useIntegral}
                      onChange={setUseIntegral}
                      disabled={accountingMode !== 'cumulative' || pendingRecharge || rechargeSubmitting || !integralConfig || integralConfig.integral_rate <= 0}
                    />
                  </div>
                  {useIntegral && integralConfig && integralConfig.integral_rate > 0 && (
                    <Alert
                      message={`转化率: ${(integralConfig.integral_rate * 100).toFixed(1)}%`}
                      description={
                        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.rechargeAmount !== curr.rechargeAmount}>
                          {({ getFieldValue }) => {
                            const amount = getFieldValue('rechargeAmount') || 0;
                            const integral = Math.floor(amount * integralConfig.integral_rate);
                            return <span className="text-size-xs text-soft">充值 ¥{amount} 可获得 <strong className="text-accent-warning">{integral}</strong> 积分</span>;
                          }}
                        </Form.Item>
                      }
                      type="info"
                      showIcon
                      icon={<InfoCircleOutlined />}
                      className="text-size-xs"
                    />
                  )}
                </Space>
              </div>
            </Col>
          </Row>

          <Divider style={{ margin: '12px 0' }} />

          <div className="section-note block-gap-md">
            <div className="section-note-text">
              <span className="section-note-strong">AOZ 发放支持三种类型。</span>
              普通 AOZ、免费 AOZ 和 GM AOZ 会分别写入不同字段，请按实际补发类型填写。
            </div>
          </div>

          <div className="row-between-center-gap block-gap-sm">
            <span><strong>AOZ 充值</strong></span>
            <Button
              type="link"
              size="small"
              icon={<SettingOutlined />}
              onClick={() => setIntegralConfigVisible(true)}
            >
              积分转化率设置
            </Button>
          </div>

          <Form.Item name="aozCoin" rules={[{ type: 'integer', min: 0, max: 2147483647 }]} label="AOZ充值">
            <InputNumber min={0} placeholder="请输入AOZ数量" className="field-width-full" />
          </Form.Item>
          <Form.Item name="freeAozCoin" rules={[{ type: 'integer', min: 0, max: 2147483647 }]} label="免费AOZ">
            <InputNumber min={0} placeholder="请输入免费AOZ数量" className="field-width-full" />
          </Form.Item>
          <Form.Item name="gmAozCoin" rules={[{ type: 'integer', min: 0, max: 2147483647 }]} label="GM AOZ">
            <InputNumber min={0} placeholder="请输入GM AOZ数量" className="field-width-full" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 充值积分转化率设置弹窗 */}
      <Modal
        title="充值积分转化率设置"
        open={integralConfigVisible}
        onCancel={() => {
          setIntegralConfigVisible(false);
          integralConfigForm.resetFields();
        }}
        onOk={() => integralConfigForm.submit()}
      >
        {integralConfig && (
          <Alert
            message="设置说明"
            description="转化率范围 0-1（如 0.1 表示 10%，充值100元获得10积分），充值积分 GlobalType ID 为游戏服务器中充值积分的道具ID"
            type="info"
            showIcon
            className="block-gap-md"
          />
        )}
        <Form form={integralConfigForm} layout="vertical" onFinish={handleIntegralConfigSubmit}>
          <div className="page-note page-note-flat">
            <div className="page-note-text">
              <span className="page-note-strong">这里只控制充值金额转积分的换算规则。</span>
              修改后会影响后续使用“充值积分”模式的发放结果。
            </div>
          </div>
          <Form.Item
            name="integral_rate"
            label="充值积分转化率（0-1之间的小数）"
            rules={[
              { required: true, message: '请输入转化率' },
              { type: 'number', min: 0, max: 1, message: '转化率必须在 0-1 之间' }
            ]}
          >
            <InputNumber
              min={0}
              max={1}
              step={0.01}
              precision={2}
              className="field-width-full"
              placeholder="例如：0.1 表示 10%"
              addonAfter="（10% = 0.1）"
            />
          </Form.Item>
          <Form.Item
            name="integral_global_type_id"
            label="充值积分 GlobalType ID"
            rules={[{ required: true, message: '请输入 GlobalType ID' }]}
          >
            <InputNumber
              min={1}
              style={{ width: '100%' }}
              placeholder="请输入充值积分的道具ID"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 发送邮件弹窗 */}
      <Modal
        title="发送邮件"
        open={mailVisible}
        onCancel={() => {
          setMailVisible(false);
          mailForm.resetFields();
        }}
        onOk={() => mailForm.submit()}
      >
        <Form form={mailForm} layout="vertical" onFinish={handleSendMailSubmit}>
          <div className="page-note" style={{ marginTop: 0, marginBottom: 16 }}>
            <div className="page-note-text">
              <span className="page-note-strong">这里发送的是单个玩家邮件。</span>
              适合客服补发、活动补偿或临时通知，不会影响全服邮件记录。
            </div>
          </div>
          <Form.Item
            name="title"
            label="邮件标题"
            rules={[{ required: true, message: '请输入邮件标题' }]}
          >
            <Input placeholder="请输入邮件标题" />
          </Form.Item>
          <Form.Item
            name="content"
            label="邮件内容"
            rules={[{ required: true, message: '请输入邮件内容' }]}
          >
            <TextArea rows={4} placeholder="请输入邮件内容" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 修改密码弹窗 */}
      <Modal
        title="修改玩家密码"
        open={resetPasswordVisible}
        onCancel={() => {
          setResetPasswordVisible(false);
          resetPasswordForm.resetFields();
        }}
        onOk={() => resetPasswordForm.submit()}
      >
        <Form form={resetPasswordForm} layout="vertical" onFinish={handleResetPasswordSubmit}>
          <div className="page-note" style={{ marginTop: 0, marginBottom: 16 }}>
            <div className="page-note-text">
              <span className="page-note-strong">修改后会立即覆盖原密码。</span>
              请确认账号归属和需求后再重置，并尽量使用临时密码进行首次通知。
            </div>
          </div>
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码长度不能少于6位' }
            ]}
          >
            <Input.Password placeholder="请输入新密码（至少6位）" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// 主组件
function UserManagement() {
  return <PlayerTab />;
}

export default UserManagement;
